import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, CheckCircle2, AlertCircle, Loader2, ExternalLink, RefreshCw, ShieldAlert, ShieldCheck, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtRel = (iso) => {
  try {
    const d = new Date(iso);
    const s = Math.round((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    return `${Math.round(s / 3600)}h ago`;
  } catch { return ""; }
};

export default function ProviderHealthWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [diagnosingKey, setDiagnosingKey] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await api.get("/providers/health");
      setData(r.data);
    } catch {
      setData({ items: [], summary: { green: 0, red: 0, managed: 0 } });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load({ silent: true });
    const t = setInterval(() => load({ silent: true }), 5 * 60 * 1000); // re-poll every 5 min
    return () => clearInterval(t);
  }, [load]);

  const diagnoseOne = async (key, label) => {
    setDiagnosingKey(key);
    const tid = `diag-${key}`;
    toast.loading(`Diagnosing ${label}…`, { id: tid });
    try {
      const r = await api.post(`/providers/${key}/diagnose`);
      if (r.data?.ok) {
        toast.success(`${label}: all checks passed.`, { id: tid });
      } else {
        toast.warning(r.data?.summary || `${label}: diagnostics found an issue.`, { id: tid, duration: 10000 });
      }
      load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Diagnostics failed for ${label}`, { id: tid });
    } finally {
      setDiagnosingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-line bg-bg-surface p-4 flex items-center gap-3" data-testid="provider-health-widget">
        <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />
        <span className="text-xs font-mono text-ink-muted">Loading provider health…</span>
      </div>
    );
  }

  const byok = (data?.items || []).filter((i) => i.mode === "byok");
  const { summary = {} } = data || {};
  const greenCount = summary.green || 0;
  const redCount = summary.red || 0;
  const totalByok = greenCount + redCount;
  const hasIssues = redCount > 0;
  const allHealthy = totalByok > 0 && redCount === 0;
  const notMonitored = totalByok === 0;

  // Pick the visual status banner shape based on the overall state.
  const banner = hasIssues
    ? {
        bg: "bg-brand-onair/10 border-brand-onair/40",
        text: "text-brand-onair",
        icon: ShieldAlert,
        label: `${redCount} ${redCount === 1 ? "provider" : "providers"} need attention`,
        sub: `${greenCount}/${totalByok} healthy`,
        pulse: true,
      }
    : allHealthy
    ? {
        bg: "bg-emerald-500/10 border-emerald-500/40",
        text: "text-emerald-300",
        icon: ShieldCheck,
        label: "All integrations healthy",
        sub: `${greenCount}/${totalByok} BYOK providers verified`,
        pulse: false,
      }
    : {
        bg: "bg-bg-elevated border-line",
        text: "text-ink-muted",
        icon: Cpu,
        label: "No BYOK providers connected",
        sub: "Connect a key to start monitoring",
        pulse: false,
      };
  const BannerIcon = banner.icon;

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="provider-health-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold flex items-center gap-2">
          <Cpu className="h-4 w-4 text-brand-blue" /> AI Provider Health
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load()}
            disabled={refreshing}
            data-testid="provider-health-refresh"
            title="Re-check all connected providers now"
            className="text-[11px] text-ink-secondary hover:text-brand-amber transition inline-flex items-center gap-1 disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh
          </button>
          <Link
            to="/dashboard/providers/alerts"
            data-testid="provider-health-alerts"
            title="Email / Slack / Discord alerts when a provider goes down"
            className="text-[11px] text-ink-secondary hover:text-brand-amber transition inline-flex items-center gap-1"
          >
            Alerts
          </Link>
          <Link
            to="/dashboard/providers"
            data-testid="provider-health-manage"
            className="text-[11px] text-brand-amber hover:underline inline-flex items-center gap-1"
          >
            Manage <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Headline status banner — surfaces the single most actionable number. */}
      <div
        className={`rounded-md border px-3 py-2.5 mb-3 flex items-center gap-3 ${banner.bg}`}
        data-testid="provider-health-banner"
        data-state={hasIssues ? "issues" : allHealthy ? "healthy" : "idle"}
      >
        <div className="relative">
          <BannerIcon className={`h-5 w-5 ${banner.text}`} />
          {banner.pulse && (
            <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-onair opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-onair" />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-semibold ${banner.text}`} data-testid="provider-health-banner-label">{banner.label}</div>
          <div className="text-[10px] text-ink-muted">{banner.sub}</div>
        </div>
        {hasIssues && (
          <Link
            to="/dashboard/providers"
            data-testid="provider-health-banner-fix"
            className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border border-brand-onair/40 text-brand-onair hover:bg-brand-onair/10"
          >
            Fix now →
          </Link>
        )}
      </div>

      {/* Three-up metric chips (kept for at-a-glance secondary stats) */}
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2" data-testid="health-green">
          <div className="font-display text-2xl font-bold text-emerald-300">{greenCount}</div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-300/80">healthy</div>
        </div>
        <div className="rounded-md border border-brand-onair/30 bg-brand-onair/5 p-2" data-testid="health-red">
          <div className="font-display text-2xl font-bold text-brand-onair">{redCount}</div>
          <div className="text-[10px] uppercase tracking-wider text-brand-onair/80">issues</div>
        </div>
        <div className="rounded-md border border-brand-amber/30 bg-brand-amber/5 p-2" data-testid="health-managed">
          <div className="font-display text-2xl font-bold text-brand-amber">{summary.managed || 0}</div>
          <div className="text-[10px] uppercase tracking-wider text-brand-amber/80">managed</div>
        </div>
      </div>

      {notMonitored ? (
        <div className="text-xs text-ink-muted">
          No BYOK keys connected yet.{" "}
          <Link to="/dashboard/providers" className="text-brand-amber hover:underline">Connect a provider</Link> to start monitoring.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {byok.map((it) => {
            const Icon = it.ok ? CheckCircle2 : AlertCircle;
            const color = it.ok ? "text-emerald-300" : "text-brand-onair";
            return (
              <div
                key={it.key}
                data-testid={`health-row-${it.key}`}
                data-ok={it.ok ? "true" : "false"}
                className="flex items-center justify-between text-xs gap-2"
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <Icon className={`h-3.5 w-3.5 ${color} shrink-0`} />
                  <span className="font-semibold text-ink-primary truncate">{it.label}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`text-[11px] ${color} truncate max-w-[180px]`} title={it.message}>
                    {it.message} · {fmtRel(it.last_checked)}
                  </span>
                  {!it.ok && (
                    <button
                      onClick={() => diagnoseOne(it.key, it.label)}
                      disabled={diagnosingKey === it.key}
                      data-testid={`health-diagnose-${it.key}`}
                      title="Run full diagnostics now"
                      className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50"
                    >
                      {diagnosingKey === it.key ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Stethoscope className="h-2.5 w-2.5" />}
                      Diagnose
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
